import React from 'react';
import styled from 'styled-components';
import {
  createHelpCenterArticleUrl,
  createHelpCenterSectionUrl,
} from '../../../../pages/HelpCenter/routes';
import {HelpCenterArticle} from '../../../../pages/HelpCenter/types';
import {useTrackOriginClick} from '../../../../tracking/hooks/useTrackOriginClick';
import Icon, {IconSize} from '../../../Icon/Icon';
import {GenericIconName} from '../../../Icon/IconName';
import {UnstyledLink} from '../../../Navigation/ClientSideLink/UnstyledLink';
import {bordersColor} from '../../../constants/colors';
import {mediaMinTablet} from '../../../constants/customMedia';
import {
  fontSize14,
  fontSize16,
  fwBold,
  fwMediumBold,
} from '../../../constants/typography';
import {FormDataRaw} from '../types';
import {FormSection} from './styled';

interface Props {
  subject: FormDataRaw['subject'] | null;
  topAnswers: HelpCenterArticle[] | null;
  labels: {
    topAnswersHeadline: (answer: string) => string;
    more: string;
    get_more_help: string;
  };
  onBeforeSelect: () => void;
}
const LinkToMoreWrapper = styled.div`
  padding-top: 12px;
`;

const LinkToMore = styled(UnstyledLink)`
  ${fontSize14};
  ${fwMediumBold};
  line-height: 1.43;
`;

const LinkToAnswer = styled(UnstyledLink)`
  padding: 18px 0 18px 20px;
  display: grid;
  grid-template: auto / 1fr 50px;
  gap: 20px;
  border: 1px solid ${bordersColor};
  margin-bottom: 8px;
  align-items: center;
  ${fontSize14};
  ${fwMediumBold};
  line-height: 1.43;
`;

const GrayIcon = styled(Icon)`
  fill: ${bordersColor};
`;

const StyledMoreArrowIcon = styled(Icon)``;
const MobileOnlyIcon = styled.span`
  position: relative;
  height: 20px;
  display: inline-block;

  @media ${mediaMinTablet} {
    display: none;
  }

  svg {
    position: absolute;
  }
`;

const TopAnswersSectionHeading = styled.div`
  ${fontSize16}
  ${fwBold}
    line-height: 1.25;
  padding: 20px 0;
`;

export const TopAnswersSection: React.FC<Props> = ({
  topAnswers,
  subject,
  labels,
  onBeforeSelect,
}) => {
  const trackOrigin = useTrackOriginClick();

  if (subject && topAnswers) {
    return (
      <FormSection>
        <TopAnswersSectionHeading data-testid="hcFAQSectionTitle">
          {labels.topAnswersHeadline(subject.label)}
        </TopAnswersSectionHeading>
        {topAnswers.map(answer => {
          return (
            <LinkToAnswer
              href={createHelpCenterArticleUrl(
                {name: subject.value.name, id: subject.value.id},
                {id: answer.id},
              )}
              key={answer.id}
              onClick={() => {
                onBeforeSelect();
                trackOrigin('contact_form_top_5_faqs');
              }}
              data-testid={`hcFAQItem-${answer.id}`}
            >
              <span>{answer.name}</span>
              <GrayIcon
                asset={GenericIconName.IcArrowSmallRight}
                size={IconSize.M}
              />
            </LinkToAnswer>
          );
        })}
        <LinkToMoreWrapper>
          <LinkToMore
            href={createHelpCenterSectionUrl(subject.value)}
            onClick={onBeforeSelect}
            data-testid={`hcFAQMoreButton`}
          >
            {labels.more} <b>{labels.get_more_help}</b>
            <MobileOnlyIcon>
              <StyledMoreArrowIcon
                asset={GenericIconName.IcArrowSmallRight}
                size={IconSize.M}
              />
            </MobileOnlyIcon>
          </LinkToMore>
        </LinkToMoreWrapper>
      </FormSection>
    );
  }

  return null;
};
